// App-bar avatar (initials circle) → small dropdown with the signed-in identity
// and a Sign out action.

import { LogOut } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function userInitials(displayName?: string | null, username?: string | null): string {
  const source = (displayName || username || "").trim();
  if (!source) return "?";
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

export function UserMenu() {
  const { displayName, username, signOut } = useAuth();

  function handleSignOut() {
    // signOut() also ends the org-wide ICM session and navigates away.
    signOut();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account"
        className="rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <Avatar size="sm" className="size-7">
          <AvatarFallback className="bg-primary/10 text-[11px] font-semibold text-primary">
            {userInitials(displayName, username)}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-1.5 py-1.5">
          <div className="truncate text-sm font-medium">{displayName || username || "Signed in"}</div>
          {username && <div className="truncate text-xs text-muted-foreground">{username}</div>}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={handleSignOut}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
